/**
 * VidStr version & changelog.
 *
 * IMPORTANT: Update this file with EVERY code change:
 *   1. Bump VERSION (format: YYYY.MM.DD.patch, patch resets each day)
 *   2. Add a new CHANGELOG entry at the top (same version + date)
 *
 * Also keep in sync:
 *   - /public/llms.txt  → update the version field (and the feature list on
 *     bigger changes)
 *
 * The changelog page (/changelog) renders directly from CHANGELOG, and the
 * version badge next to the logo renders VERSION — no other files to touch.
 */

export const VERSION = '2026.08.25.14';

export interface ChangelogEntry {
  version: string;
  date: string;
  title: string;
  changes: string[];
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    version: '2026.08.25.14',
    date: '2026-08-25',
    title: 'Remove dead relay.nostr.band',
    changes: [
      'Removed relay.nostr.band from the default relay list — the relay is long dead. NIP-50 search now relies on relay.divine.video (DiVine\'s relay) in the default read set.',
      'Search documentation updated accordingly.',
    ],
  },
  {
    version: '2026.08.25.13',
    date: '2026-08-25',
    title: 'Fix kind 21/22 videos squeezed out by combined-kind query limits',
    changes: [
      'Root cause of "only addressable and legacy videos show up": feeds sent ONE filter combining all kinds with ONE limit, and relays apply limit per filter — so high-volume addressable kinds (34235/34236) filled the whole result window and regular kind 21/22 never made the cut. On profiles, an author\'s addressable uploads similarly hid their regular ones.',
      'All feeds now send one filter per kind inside the same REQ (NIP-01 evaluates limit per filter object), giving every kind its own per-relay event budget — videos, shorts, notes, plebs, hashtag, and search feeds alike.',
    ],
  },
  {
    version: '2026.08.25.12',
    date: '2026-08-25',
    title: 'Fix missing videos: wider relay coverage, hardier parser',
    changes: [
      'Many regular video events (kind 21/22) never reached the feed because the default read set was too small (4 relays, primal.net write-only). Default relays now read from relay.divine.video (DiVine\'s relay, NIP-50 capable), relay.damus.io, relay.snort.social, relay.primal.net, nostr.wine, nos.lol, relay.ditto.pub, relay.dreamith.to, and relay.nostr.band.',
      'The imeta parser now also accepts the split form ["imeta", "url", "https://…"] (key and value as separate array elements) in addition to the standard space-joined form — such events were previously dropped entirely.',
      'Video events whose URL only appears in the content text are now detected via the same content scan used for kind 1 note videos, instead of being dropped.',
      'blossom:<sha256>.<ext> URIs (BUD-10) are resolved to HTTPS URLs on the default Blossom server so such videos actually play.',
      'Search results are now always verified client-side against title and description — relays without NIP-50 support ignore the search field and were returning unfiltered recent videos.',
    ],
  },
  {
    version: '2026.08.25.11',
    date: '2026-08-25',
    title: 'Plebs tab on profile pages',
    changes: [
      'Profile pages now also have a "Plebs" tab showing that user\'s plebs.app uploads (#pv69420), deduped by video URL the same way as the main page tab (addressable > regular > legacy > note; single surviving events are shown).',
      'Shared the plebs.app dedupe/hashtag helpers via lib/videoTypes.ts.',
    ],
  },
  {
    version: '2026.08.25.10',
    date: '2026-08-25',
    title: 'plebs.app archive tab (#pv69420)',
    changes: [
      'New "Plebs" tab on the main page: videos uploaded via the defunct plebs.app client, which published each video as three events (regular, addressable, kind 1 note) tagged #pv69420.',
      'The tab queries all video kinds plus kind 1 with a relay-side #t filter and dedupes by video URL, preferring the addressable version, then regular, then legacy, then the note.',
      'Videos where only one or two of the three events survived are still shown — dedupe only collapses true duplicates.',
      'Works with the Global/Subscribed scopes and all feed filters.',
    ],
  },
  {
    version: '2026.08.25.9',
    date: '2026-08-25',
    title: 'Note videos: kind 1 notes with linked video files',
    changes: [
      'VidStr now supports kind 1 notes that link a video file — via an imeta tag with a video MIME type, or a video file URL (mp4, webm, mov, m4v, mkv, avi, ogv, m3u8) in the note content. Replies are excluded.',
      'New "Notes" tab on the main page (with Global/Subscribed scopes) and on profile pages. Note videos play in the same player, support reactions, comments, reports, and content warnings, and are labelled "Note (kind 1)".',
      'Since relays cannot filter kind 1 by content, detection is client-side; the Notes feed fetches larger batches (200 per relay) to keep the yield decent.',
      'New feed filter toggle "Show videos from notes" (default on) — turning it off hides the Notes tab entirely.',
      'Titles for note videos are derived from the note text (URL removed), falling back to the file name.',
    ],
  },
  {
    version: '2026.08.25.8',
    date: '2026-08-25',
    title: 'Published as a Nostr app',
    changes: [
      'VidStr is now published as a Nostr app (NIP-89 kind 31990) with icon, banner, and NIP-19 web handlers for note/nevent/naddr/npub/nprofile identifiers.',
      'Added README.md with an "Edit with Marlowe" badge and a project overview.',
    ],
  },
  {
    version: '2026.08.25.7',
    date: '2026-08-25',
    title: 'PWA with offline support, new icon, og image',
    changes: [
      'VidStr is now an installable PWA: a service worker precaches the app shell and runtime-caches same-origin assets, so the app works fully offline after the first load. Updates activate silently — no prompts or forced reloads.',
      'New app icon (red rounded square with play button) used for the PWA manifest (any + maskable) and Apple touch icon.',
      'New Open Graph share image (og:image) plus og:url, Twitter card, and theme-color metadata.',
      'The changelog page now links to the original VidStr (nsite archive).',
    ],
  },
  {
    version: '2026.08.25.6',
    date: '2026-08-25',
    title: 'Version badge visible on mobile',
    changes: [
      'The version badge was hidden below the sm breakpoint because the header row is too crowded on mobile. It now appears in the mobile search row next to the search button (still linking to /changelog), while staying next to the logo on larger screens.',
    ],
  },
  {
    version: '2026.08.25.5',
    date: '2026-08-25',
    title: 'Subscribe button, llms.txt, changelog page, version badge',
    changes: [
      'Profile pages now have a Subscribe/Unsubscribe button (NIP-02 kind 3 contact list) when logged in. The freshest contact list is fetched before publishing so petnames and relay metadata are preserved.',
      'Added /public/llms.txt — machine-readable project documentation for AI agents, following the llms.txt spec.',
      'Added /changelog page rendering the full version history from lib/version.ts.',
      'The version number is now shown next to the VidStr logo in the top-left header.',
      'AGENTS.md now documents the project and mandates version + changelog updates on every code change.',
    ],
  },
  {
    version: '2026.08.25.4',
    date: '2026-08-25',
    title: 'Ditto theme background images with translucent overlay',
    changes: [
      'App-wide Ditto themes now render their bg image on the page with the theme background color as a semi-transparent overlay (fixed to viewport).',
      'Profile pages render the theme bg image across the themed header section with the same translucent overlay.',
      'The kind 0 banner image always keeps priority on the profile banner strip — theme bg images never replace it.',
      'Theme and banner URLs are normalized through URL parsing before CSS interpolation (https-only, percent-encoded).',
    ],
  },
  {
    version: '2026.08.25.3',
    date: '2026-08-25',
    title: 'Theme gallery in Settings',
    changes: [
      'Settings → Appearance now lists available Ditto themes (kind 36767) found on your relays as a browsable gallery with live mini previews, color swatches, and author names.',
      'One click applies a theme app-wide; the VidStr Default card resets.',
      'Removed the manual naddr input — the identifier is derived from the selected theme event.',
    ],
  },
  {
    version: '2026.08.25.2',
    date: '2026-08-25',
    title: 'Orientation-separated feeds (Videos vs Shorts)',
    changes: [
      'Horizontal (kind 21/34235) and vertical (22/34236) videos are now queried with separate relay filters and shown in dedicated feeds, so floods of vertical videos (e.g. from DiVine) can no longer push horizontal videos out of relay limit windows.',
      'Main page: Videos (default) / Shorts tabs with Global / Subscribed scope chips.',
      'Shorts render in a dense 9:16 grid; profile, hashtag, and search pages got the same Videos/Shorts split.',
      'The related-videos sidebar on watch pages now only suggests horizontal videos.',
      'Fixed mobile header overlap on search/hashtag pages with long queries (header now stacks into two rows).',
    ],
  },
  {
    version: '2026.08.25.0',
    date: '2026-08-25',
    title: 'VidStr rebuilt on the current stack',
    changes: [
      'Complete rebuild of VidStr as a NIP-71 video client with a YouTube-like UI.',
      'Supports regular (21/22), addressable (34235/34236), and legacy (no-imeta) video events.',
      'Anonymous uploads with one-time keys (keep or toss after publishing) alongside normal logged-in uploads.',
      'Simultaneous multi-server Blossom uploads with an overall progress bar, a current-server progress bar, and per-server status.',
      'Full HLS/m3u8 playback via hls.js, including nested master playlists, with CORS-proxy rewriting and quality selection.',
      'Video length determination: from event tags, by probing file metadata, or by summing m3u8 EXTINF durations (recursively).',
      'YouTube-link videos show a warning banner about NIP-71 non-compliance and decentrality.',
      'Feed filters: reported videos (hidden by default), YouTube-link videos, videos without determinable duration.',
      'Global and subscribed feeds with per-relay streaming (10 s EOSE window, live updates after EOSE) and cursor pagination.',
      'NIP-25 reactions with separate +/−/custom-emoji buttons and NIP-30 custom emoji tags.',
      'Comments: NIP-22 (kind 1111) and legacy kind 1 displayed together; kind 1 marked non-standard with a composer warning.',
      'NIP-56 (kind 1984) reporting for videos and profiles; NIP-36 content warnings gating thumbnails and players.',
      'Ditto theme support: kind 16767 on profile headers, kind 36767 app-wide — colors and fonts only, not the rounded Ditto UI.',
      'Delete videos (NIP-09 kind 5); edit addressable videos by re-publishing with the same d tag.',
      'Hashtag pages and title/description search (NIP-50 where relays support it, plus client-side matching).',
      'Settings page for relays (NIP-65 kind 10002), Blossom servers (kind 10063), and appearance.',
    ],
  },
];
